// src/client/screens/borrower/LoanSuccessScreen.tsx
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Check } from 'lucide-react';
import { ROUTES } from '@/constants/routes';
import { useActiveLoan } from '@client/hooks/useLoan';
import { ngweeToKwachaNumber } from '@client/utils/currency';
import { computeRepaymentSchedule } from '@client/utils/loanUtils';
import { clearLoanDraft, readLoanDraft, type LoanDraft } from '@client/lib/loanDraft';
import Button from '@client/components/Button';

export default function LoanSuccessScreen() {
  const navigate = useNavigate();
  const { data: loan } = useActiveLoan();

  // Capture draft synchronously *before* the cleanup effect clears it, so we
  // can show the borrower's amount on the success screen even if the active
  // loan query hasn't refreshed yet.
  const [draft] = useState<LoanDraft | null>(() => readLoanDraft());
  useEffect(() => { clearLoanDraft(); }, []);

  // Prefer the freshly-issued loan if the cache has it; otherwise fall back
  // to the captured draft.
  const principalKwacha = loan
    ? ngweeToKwachaNumber(loan.principal_ngwee)
    : draft
      ? draft.principalNgwee / 100
      : null;

  const reference = loan?.loan_number ?? 'Pending';

  // First payment date — from server schedule if available, else previewed.
  const firstPayment = (() => {
    if (loan?.due_at) return new Date(loan.due_at);
    if (draft) {
      const sched = computeRepaymentSchedule(
        draft.principalNgwee as never,
        draft.termDays,
        new Date().toISOString(),
      );
      return new Date(sched[0]?.dueAt ?? Date.now());
    }
    return null;
  })();

  return (
    <div style={{
      minHeight: '100dvh',
      background: 'var(--c-bg)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      textAlign: 'center',
      padding: '80px 32px 32px',
      maxWidth: 480,
      margin: '0 auto',
    }}>
      <div style={{ position: 'relative', width: 120, height: 120, marginTop: 40 }}>
        <div style={{
          position: 'absolute', inset: 0,
          borderRadius: '50%', background: 'var(--c-accent)',
        }} />
        <div style={{
          position: 'absolute', inset: 14,
          borderRadius: '50%', background: 'var(--c-bg)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Check size={42} color="var(--c-accent)" strokeWidth={2.4} />
        </div>
      </div>

      <h1 style={{
        fontFamily: 'var(--ff-display)',
        fontSize: 48,
        color: 'var(--c-text)',
        letterSpacing: -1.5,
        marginTop: 36,
        lineHeight: 1,
        fontWeight: 400,
        margin: '36px 0 0',
      }}>
        {principalKwacha != null ? (
          <>
            <span style={{ fontWeight: 600 }}>K{principalKwacha.toLocaleString()}</span> on the way.
          </>
        ) : (
          'You’re all set.'
        )}
      </h1>
      <p style={{ marginTop: 14, fontSize: 14, color: 'var(--c-text-muted)', lineHeight: 1.5 }}>
        Sent to your registered mobile money. Usually arrives in under 5 minutes.
      </p>

      <div style={{
        marginTop: 32,
        width: '100%',
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 10,
      }}>
        <div style={{
          background: 'var(--c-surface)',
          border: '1px solid var(--c-border)',
          borderRadius: 14,
          padding: 14,
          textAlign: 'left',
        }}>
          <div className="eyebrow" style={{ fontSize: 10, letterSpacing: 1 }}>Reference</div>
          <div style={{
            fontFamily: 'var(--ff-mono)',
            fontSize: 14,
            color: 'var(--c-text)',
            marginTop: 4,
            fontWeight: 500,
          }}>
            {reference}
          </div>
        </div>
        <div style={{
          background: 'var(--c-surface)',
          border: '1px solid var(--c-border)',
          borderRadius: 14,
          padding: 14,
          textAlign: 'left',
        }}>
          <div className="eyebrow" style={{ fontSize: 10, letterSpacing: 1 }}>First payment</div>
          <div style={{
            fontFamily: 'var(--ff-mono)',
            fontSize: 14,
            color: 'var(--c-text)',
            marginTop: 4,
            fontWeight: 500,
          }}>
            {firstPayment ? firstPayment.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '—'}
          </div>
        </div>
      </div>

      <div style={{ flex: 1 }} />

      <Button
        block size="lg"
        onClick={() => navigate(ROUTES.home)}
        rightIcon={<ArrowRight size={18} />}
      >
        Back to home
      </Button>
      <Button
        block
        variant="ghost"
        style={{ marginTop: 10 }}
        onClick={() => {
          if (typeof navigator !== 'undefined' && navigator.share) {
            void navigator.share({
              title: 'Thandizo loan receipt',
              text: `Loan ${reference} · K${principalKwacha?.toLocaleString() ?? '—'} — sent to your mobile money.`,
            });
          }
        }}
      >
        Share receipt
      </Button>
    </div>
  );
}
